import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, MapPin, Plus, Trash2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import Header from './Header';
import Footer from './Footer';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateProfile, addAddress, updateAddress, deleteAddress, setDefaultAddress, logout } = useAuth();
  const { t } = useTranslation();
  
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    phone: user?.phone || ''
  });
  
  const [newAddress, setNewAddress] = useState({
    address: '',
    city: 'Liepāja',
    zip_code: 'LV-3401'
  });
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef(null);

  const handleProfileUpdate = async () => {
    const result = await updateProfile(profileData);
    if (result.success) {
      setEditingProfile(false);
    }
  };

  const searchAddress = async (query) => {
    if (!query || query.length < 2) {
      setAddressSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      setIsSearching(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(
        `${BACKEND_URL}/api/geocoding/address/search?query=${encodeURIComponent(query + ', Liepāja')}`,
        { signal: AbortSignal.timeout(5000) }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch addresses');
      }
      
      const data = await response.json();
      
      // Show ALL results from API (like rozedinapoli.lv)
      // If no results, add manual entry option
      let suggestions = data.length > 0 ? data : [{
        display_name: `${query}, Liepāja, LV-3401, Latvija`,
        address: {
          road: query,
          city: 'Liepāja',
          postcode: 'LV-3401'
        },
        manual: true
      }];

      setAddressSuggestions(suggestions);
      setShowSuggestions(suggestions.length > 0);
    } catch (error) {
      console.error('Error searching address:', error);
      // On error, allow manual entry
      setAddressSuggestions([{
        display_name: `${query}, Liepāja, LV-3401, Latvija`,
        address: {
          road: query,
          city: 'Liepāja',
          postcode: 'LV-3401'
        },
        manual: true
      }]);
      setShowSuggestions(true);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddressInputChange = (value) => {
    setNewAddress({ ...newAddress, address: value });
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (value.length >= 2) {
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(() => {
        searchAddress(value);
      }, 500);
    } else {
      setShowSuggestions(false);
      setAddressSuggestions([]);
      setIsSearching(false);
    }
  };

  const selectAddress = (suggestion) => {
    // Get full address from suggestion
    const address = suggestion.address || {};
    let fullAddress = '';
    
    // If it's a manual entry, use the road field directly
    if (suggestion.manual) {
      fullAddress = address.road || newAddress.address;
    } else {
      // Try to build address from structured data if available
      if (address.road) {
        fullAddress = address.road;
        if (address.house_number) {
          fullAddress += ' ' + address.house_number;
        } else {
          // If no house number from API, try to extract from user's input
          const userInput = newAddress.address.trim();
          const numberMatch = userInput.match(/\d+/);
          if (numberMatch) {
            fullAddress += ' ' + numberMatch[0];
          }
        }
      } else if (suggestion.display_name) {
        // Fallback to first part of display_name
        fullAddress = suggestion.display_name.split(',')[0];
      }
    }
    
    setNewAddress({
      address: fullAddress,
      city: 'Liepāja',
      zip_code: address.postcode || 'LV-3401'
    });
    setShowSuggestions(false);
    setAddressSuggestions([]);
  };

  const handleAddAddress = async () => {
    if (!newAddress.address || !newAddress.city) {
      alert(t('fillAllFields'));
      return;
    }

    const result = await addAddress({
      ...newAddress,
      is_default: (user?.addresses || []).length === 0
    });
    
    if (result.success) {
      setNewAddress({ address: '', city: '', zip_code: '' });
      setShowAddAddress(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (window.confirm(t('confirmDeleteAddress'))) {
      await deleteAddress(addressId);
    }
  };

  const handleSetDefault = async (addressId) => {
    await setDefaultAddress(addressId);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">{t('pleaseLogin')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header cartItemCount={0} onCartClick={() => {}} onOrderClick={() => {}} />
      
      <div className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('backToHome')}
            </Button>

            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-50"
            >
              {t('logout')}
            </Button>
          </div>

          <h1 className="text-5xl font-bold text-black mb-8">{t('myAccount')}</h1>

          <Card className="border-2 border-gray-200 mb-6">
            <CardContent className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start space-x-4">
                  <User className="w-8 h-8 text-black mt-1" />
                  <div>
                    <h2 className="text-2xl font-bold text-black mb-4">{t('accountInformation')}</h2>
                  </div>
                </div>
                <Button
                  onClick={() => setEditingProfile(!editingProfile)}
                  variant="outline"
                  size="sm"
                >
                  {editingProfile ? t('cancel') : t('edit')}
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {t('email')}
                  </label>
                  <p className="text-lg text-gray-900">{user.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {t('name')}
                  </label>
                  {editingProfile ? (
                    <Input
                      value={profileData.name}
                      onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                      className="max-w-md"
                    />
                  ) : (
                    <p className="text-lg text-gray-900">{user.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    {t('phone')}
                  </label>
                  {editingProfile ? (
                    <Input
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="max-w-md"
                    />
                  ) : (
                    <p className="text-lg text-gray-900">{user.phone}</p>
                  )}
                </div>

                {editingProfile && (
                  <Button
                    onClick={handleProfileUpdate}
                    className="bg-black text-white hover:bg-gray-800"
                  >
                    {t('saveChanges')}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-gray-200">
            <CardContent className="p-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-8 h-8 text-black mt-1" />
                  <h2 className="text-2xl font-bold text-black">{t('savedAddresses')}</h2>
                </div>
                <Button
                  onClick={() => setShowAddAddress(!showAddAddress)}
                  variant="outline"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('addAddress')}
                </Button>
              </div>

              {showAddAddress && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-3">
                  <div className="relative">
                    <Input
                      placeholder={t('address')}
                      value={newAddress.address}
                      onChange={(e) => handleAddressInputChange(e.target.value)}
                      onFocus={() => newAddress.address.length >= 2 && setShowSuggestions(true)}
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                      </div>
                    )}
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {addressSuggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => selectAddress(suggestion)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-100 border-b border-gray-100 last:border-0 transition-colors"
                          >
                            <p className="font-medium text-gray-900">{suggestion.display_name.split(',')[0]}</p>
                            <p className="text-sm text-gray-500">{suggestion.display_name}</p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      placeholder={t('city')}
                      value={newAddress.city}
                      onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                      readOnly
                      className="bg-gray-100"
                    />
                    <Input
                      placeholder={t('zipCode')}
                      value={newAddress.zip_code}
                      onChange={(e) => setNewAddress({ ...newAddress, zip_code: e.target.value })}
                      readOnly
                      className="bg-gray-100"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button onClick={handleAddAddress} className="bg-black text-white hover:bg-gray-800">
                      {t('save')}
                    </Button>
                    <Button onClick={() => setShowAddAddress(false)} variant="outline">
                      {t('cancel')}
                    </Button>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {user?.addresses && user.addresses.length > 0 ? (
                  user.addresses.map((address) => (
                    <div
                      key={address.id}
                      className={`p-4 border-2 rounded-lg ${
                        address.is_default ? 'border-black bg-gray-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          {address.is_default && (
                            <span className="inline-block px-3 py-1 bg-black text-white text-xs font-semibold rounded-full mb-2">
                              {t('default')}
                            </span>
                          )}
                          <p className="text-lg font-semibold text-black">{address.address}</p>
                          <p className="text-gray-600">{address.city}, {address.zip_code}</p>
                        </div>
                        <div className="flex space-x-2">
                          {!address.is_default && (
                            <Button
                              onClick={() => handleSetDefault(address.id)}
                              variant="outline"
                              size="sm"
                              title={t('setAsDefault')}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDeleteAddress(address.id)}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-8">{t('noSavedAddresses')}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProfilePage;
